import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useUser, useOrganization } from '@clerk/clerk-react';
import { faIndustry, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { useBranding } from '../App/hooks/useBranding';
import { useCheckOnboarding } from './hooks';
import CompanyTypeCard from './components/CompanyTypeCard';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import logo from 'assets/icons/logo.png';
import { trackFreeTrialInitialization } from 'helpers/mixpanel';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { hasFreeTierInvite } from 'modules/Onboarding/utils/freeTierInvite';
import { useTranslation } from 'react-i18next';

/**
 * Company Type Selection — first step of onboarding.
 * Visual language matches the rest of the modern app (Radix/CgDialog/v2 forms):
 * soft slate backgrounds, rounded-2xl cards, subtle hover states, and the
 * primaryColor accent only where it carries meaning.
 */
const OnboardingTypeSelection = () => {
  const { t } = useTranslation('onboarding');
  const history = useHistory();
  const brand = useBranding();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { data, isLoading: isCheckingOnboarding } = useCheckOnboarding();

  useEffect(() => {
    if (user) {
      trackFreeTrialInitialization(user);
    }
  }, [user]);

  // Free-tier supplier steering: an inbound declarant invite / self-serve link
  // pre-selects the operator path — skip the type picker and deep-link the
  // operator wizard. The backend derives the restricted plan from the claimed
  // invite, so the client never asserts the plan here.
  useEffect(() => {
    if (!isCheckingOnboarding && !data?.hasOrganization && hasFreeTierInvite()) {
      history.replace(`/onboarding/wizard?type=${OrganizationType.Operator}`);
    }
  }, [isCheckingOnboarding, data, history]);

  useEffect(() => {
    if (data?.hasOrganization && organization) {
      const orgType = organization?.publicMetadata?.type;
      let redirectUrl = '/overview';

      if (orgType === OrganizationType.Consultant) {
        redirectUrl = '/consultant-operators';
      } else if (orgType === OrganizationType.Operator) {
        redirectUrl = '/operator-installations';
      }

      history.replace(redirectUrl);
    }
  }, [data, organization, history]);

  const handleTypeSelect = (type) => {
    history.push(`/onboarding/wizard?type=${type}`);
  };

  if (isCheckingOnboarding) {
    return <CgPageLoader variant="fullscreen" />;
  }

  if (data?.hasOrganization) {
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 flex flex-col">
      {/* Subtle decorative gradient — soft, doesn't compete with the cards */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-primaryColor/5 to-transparent pointer-events-none"
      />

      <div className="relative flex-1 flex items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-10">
            <img
              src={brand.logoUrl || logo}
              alt={t('onboarding:typeSelect.logoAlt', '{{brandName}} logo', {
                brandName: brand.name,
              })}
              className="h-10 mx-auto mb-8"
            />
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primaryColor/10 text-primaryColor text-[10px] font-bold uppercase tracking-wider mb-4">
              {t('actions.getStarted', 'Get started')}
            </span>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-800 mb-3 tracking-tight">
              {t('typeSelect.welcome', "Welcome — let's set up your account")}
            </h1>
            <p className="text-base text-slate-500 max-w-lg mx-auto">
              {t(
                'wizard.tailorHint',
                'Tell us about your company so we can tailor the platform to your CBAM workflow.',
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <CompanyTypeCard
              type="operator"
              title={t('type.operator', 'Operator')}
              icon={faIndustry}
              description={t(
                'typeSelect.operatorDescription',
                'You produce and/or sell CBAM goods to importers in the EU.',
              )}
              highlights={[
                t(
                  'typeSelect.operatorHighlight1',
                  'Visually map your production processes with our diagramming tool',
                ),
                t(
                  'typeSelect.operatorHighlight2',
                  'Automate embedded emissions and free allocation calculations',
                ),
                t(
                  'typeSelect.operatorHighlight3',
                  'Generate verification-ready operator reports and monitoring plans',
                ),
              ]}
              onClick={() => handleTypeSelect('operator')}
            />

            <CompanyTypeCard
              type="declarant"
              title={t('type.declarant', 'Declarant')}
              icon={faFileAlt}
              description={t(
                'typeSelect.declarantDescription',
                'You import CBAM goods into the EU and need to file declarations.',
              )}
              highlights={[
                t(
                  'typeSelect.declarantHighlight1',
                  'Generate fully compliant CBAM reports in a few clicks',
                ),
                t(
                  'typeSelect.declarantHighlight2',
                  'Forecast CBAM costs and estimate procurement scenarios',
                ),
                t(
                  'typeSelect.declarantHighlight3',
                  'Request and track emissions data from your suppliers',
                ),
              ]}
              onClick={() => handleTypeSelect('declarant')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTypeSelection;
